import React from "react";
import { Panel, DefaultButton, Checkbox, Dropdown, IDropdownOption, SpinButton, TextField } from "@fluentui/react";
import { RetrievalMode, RetrievalReasoningEffort } from "../../../api/models";
import { TokenClaimsDisplay } from "../../../components/TokenClaimsDisplay";
import { allProductAutoSelectStaticDefaults } from "../../../authConfig";
import i18next from "../../../i18n/i18n";
import styles from "../Chat.module.css";

interface SettingsPanelProps {
    isConfigPanelOpen: boolean;
    setIsConfigPanelOpen: (value: boolean) => void;
    hidetoken: boolean;

    // Settings values
    useSemanticRanker: boolean;
    useSemanticCaptions: boolean;
    useSuggestFollowupQuestions: boolean;
    useOidSecurityFilter: boolean;
    useGroupsSecurityFilter: boolean;
    retrieveCount: number;
    agenticRetrievalOutputSize?: number;
    agenticRetrievalDocumentCount?: number;
    agenticRetrievalSourceDocumentCount?: number;
    promptTemplate?: string;
    excludeCategory?: string;
    retrievalMode: RetrievalMode;
    cosmosDBRetrievalMode?: RetrievalMode;
    shouldStream: boolean;
    onlyCitations: boolean;
    useAgenticKnowledgebase: boolean;
    enableAllProductForAllUsers?: boolean;
    enableAllProductForUser?: boolean;
    allProductAutoSelectEnabled?: boolean;
    allProductAutoSelectMinConfidence?: number;
    allProductAutoSelectMinMargin?: number;
    allProductAutoSelectMinHits?: number;
    allProductAutoSelectMinAvgRerankerScore?: number;
    allProductAutoSelectMinAvgRerankerGap?: number;
    allProductAutoSelectWeightAverage?: number;
    allProductAutoSelectWeightPeak?: number;
    allProductAutoSelectWeightVolume?: number;
    retrievalReasoningEffort: RetrievalReasoningEffort;

    // Options
    retrievalOptions: IDropdownOption[];
    cosmosDBRetrievalOptions?: IDropdownOption[];
    retrievalReasoningOptions: IDropdownOption[];

    // Handlers
    onRetrieveCountChange: (ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => void;
    onAgenticRetrievalOutputSizeChange: (ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => void;
    onAgenticRetrievalOutputSizeBlur?: (ev?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onAgenticRetrievalDocumentCountChange: (ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => void;
    onAgenticRetrievalDocumentCountBlur?: (ev?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onAgenticRetrievalSourceDocumentCountChange: (ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => void;
    onAgenticRetrievalSourceDocumentCountBlur?: (ev?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onRetrievalModeChange: (ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<RetrievalMode> | undefined) => void;
    onCosmosDBRetrievalModeChange?: (ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<RetrievalMode> | undefined) => void;
    onUseSemanticRankerChange: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onUseSemanticCaptionsChange: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onShouldStreamChange: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onPromptTemplateChange?: (ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => void;
    onExcludeCategoryChanged?: (ev?: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => void;
    onUseSuggestFollowupQuestionsChange: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onUseOidSecurityFilterChange: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onUseGroupsSecurityFilterChange: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onOnlyCitationsChange: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onUseAgenticKnowledgebaseChange: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onEnableAllProductForAllUsersChange?: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onEnableAllProductForUserChange?: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onAllProductAutoSelectEnabledChange?: (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => void;
    onAllProductAutoSelectMinConfidenceChange?: (ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => void;
    onAllProductAutoSelectMinMarginChange?: (ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => void;
    onAllProductAutoSelectMinHitsChange?: (ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => void;
    onAllProductAutoSelectMinAvgRerankerScoreChange?: (ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => void;
    onAllProductAutoSelectMinAvgRerankerGapChange?: (ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => void;
    onAllProductAutoSelectWeightAverageChange?: (ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => void;
    onAllProductAutoSelectWeightPeakChange?: (ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => void;
    onAllProductAutoSelectWeightVolumeChange?: (ev?: React.SyntheticEvent<HTMLElement, Event>, newValue?: string) => void;
    onRetrievalReasoningEffortChange?: (ev: React.FormEvent<HTMLDivElement>, option?: IDropdownOption<RetrievalReasoningEffort> | undefined) => void;
}

const parsePanelDefaultNumber = (value: unknown, minimum: number, maximum: number): number => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return minimum;
    return Math.min(maximum, Math.max(minimum, numericValue));
};

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
    isConfigPanelOpen,
    setIsConfigPanelOpen,
    hidetoken,
    useSemanticRanker,
    useSemanticCaptions,
    useSuggestFollowupQuestions,
    useOidSecurityFilter,
    useGroupsSecurityFilter,
    retrieveCount,
    agenticRetrievalOutputSize,
    agenticRetrievalDocumentCount,
    agenticRetrievalSourceDocumentCount,
    promptTemplate = "",
    excludeCategory = "",
    retrievalMode,
    retrievalReasoningEffort = RetrievalReasoningEffort.Low,
    cosmosDBRetrievalMode = RetrievalMode.Vectors,
    shouldStream,
    onlyCitations,
    useAgenticKnowledgebase,
    enableAllProductForAllUsers = false,
    enableAllProductForUser = true,
    allProductAutoSelectEnabled = allProductAutoSelectStaticDefaults.enabled !== false,
    allProductAutoSelectMinConfidence = parsePanelDefaultNumber(allProductAutoSelectStaticDefaults.min_confidence, 0, 100),
    allProductAutoSelectMinMargin = parsePanelDefaultNumber(allProductAutoSelectStaticDefaults.min_margin, 0, 100),
    allProductAutoSelectMinHits = parsePanelDefaultNumber(allProductAutoSelectStaticDefaults.min_hits, 1, 99),
    allProductAutoSelectMinAvgRerankerScore = parsePanelDefaultNumber(allProductAutoSelectStaticDefaults.min_avg_reranker_score ?? 2.5, 0, 10),
    allProductAutoSelectMinAvgRerankerGap = parsePanelDefaultNumber(allProductAutoSelectStaticDefaults.min_avg_reranker_gap ?? 0.2, 0, 10),
    allProductAutoSelectWeightAverage = parsePanelDefaultNumber(allProductAutoSelectStaticDefaults.weight_average, 0, 1),
    allProductAutoSelectWeightPeak = parsePanelDefaultNumber(allProductAutoSelectStaticDefaults.weight_peak, 0, 1),
    allProductAutoSelectWeightVolume = parsePanelDefaultNumber(allProductAutoSelectStaticDefaults.weight_volume, 0, 1),
    retrievalOptions,
    cosmosDBRetrievalOptions = [],
    retrievalReasoningOptions = [],
    onRetrieveCountChange,
    onAgenticRetrievalOutputSizeChange,
    onAgenticRetrievalOutputSizeBlur,
    onAgenticRetrievalDocumentCountChange,
    onAgenticRetrievalDocumentCountBlur,
    onAgenticRetrievalSourceDocumentCountChange,
    onAgenticRetrievalSourceDocumentCountBlur,
    onRetrievalModeChange,
    onCosmosDBRetrievalModeChange,
    onUseSemanticRankerChange,
    onUseSemanticCaptionsChange,
    onShouldStreamChange,
    onPromptTemplateChange = () => undefined,
    onExcludeCategoryChanged = () => undefined,
    onUseSuggestFollowupQuestionsChange,
    onUseOidSecurityFilterChange,
    onUseGroupsSecurityFilterChange,
    onOnlyCitationsChange,
    onUseAgenticKnowledgebaseChange,
    onEnableAllProductForAllUsersChange = () => undefined,
    onEnableAllProductForUserChange = () => undefined,
    onAllProductAutoSelectEnabledChange = () => undefined,
    onAllProductAutoSelectMinConfidenceChange = () => undefined,
    onAllProductAutoSelectMinMarginChange = () => undefined,
    onAllProductAutoSelectMinHitsChange = () => undefined,
    onAllProductAutoSelectMinAvgRerankerScoreChange = () => undefined,
    onAllProductAutoSelectMinAvgRerankerGapChange = () => undefined,
    onAllProductAutoSelectWeightAverageChange = () => undefined,
    onAllProductAutoSelectWeightPeakChange = () => undefined,
    onAllProductAutoSelectWeightVolumeChange = () => undefined,
    onRetrievalReasoningEffortChange
}) => {
    return (
        <Panel
            headerText={i18next.t("chat.developerSettings")}
            isOpen={isConfigPanelOpen}
            isBlocking={false}
            onDismiss={() => setIsConfigPanelOpen(false)}
            closeButtonAriaLabel="Close"
            onRenderFooterContent={() => <DefaultButton onClick={() => setIsConfigPanelOpen(false)}>{i18next.t("chat.close")}</DefaultButton>}
            isFooterAtBottom={true}
        >
            <TextField
                className={styles.chatSettingsSeparator}
                value={promptTemplate}
                label={i18next.t("settings.overridePromptTemplate")}
                multiline
                autoAdjustHeight
                onChange={onPromptTemplateChange}
            />

            <SpinButton
                className={styles.chatSettingsSeparator}
                label={i18next.t("settings.retrieveSearchResults")}
                min={1}
                max={50}
                defaultValue={(retrieveCount ?? 5).toString()}
                onChange={onRetrieveCountChange}
            />

            <TextField
                className={styles.chatSettingsSeparator}
                value={excludeCategory}
                label={i18next.t("settings.excludeCategory")}
                onChange={onExcludeCategoryChanged}
            />

            <Checkbox
                className={styles.chatSettingsSeparator}
                checked={useSemanticRanker}
                label={i18next.t("filters.semanticRanker")}
                onChange={onUseSemanticRankerChange}
            />

            <Checkbox
                className={styles.chatSettingsSeparator}
                checked={useSemanticCaptions}
                label={i18next.t("settings.queryContextualSummaries")}
                onChange={onUseSemanticCaptionsChange}
                disabled={!useSemanticRanker}
            />

            <Checkbox
                className={styles.chatSettingsSeparator}
                checked={useSuggestFollowupQuestions}
                label={i18next.t("answer.followupQuestions")}
                onChange={onUseSuggestFollowupQuestionsChange}
                disabled={onlyCitations}
            />

            {hidetoken && (
                <Checkbox
                    className={styles.chatSettingsSeparator}
                    checked={useOidSecurityFilter}
                    label={i18next.t("settings.oidSecurityFilter")}
                    disabled={true}
                    onChange={onUseOidSecurityFilterChange}
                />
            )}

            {hidetoken && (
                <Checkbox
                    className={styles.chatSettingsSeparator}
                    checked={useGroupsSecurityFilter}
                    label={i18next.t("settings.groupsSecurityFilter")}
                    disabled={true}
                    onChange={onUseGroupsSecurityFilterChange}
                />
            )}

            <Dropdown
                className={styles.chatSettingsSeparator}
                label={i18next.t("filters.retrievalMode")}
                options={retrievalOptions}
                required
                onChange={onRetrievalModeChange}
                selectedKey={retrievalMode}
            />

            <Dropdown
                className={styles.chatSettingsSeparator}
                label={i18next.t("settings.cosmosDbRetrievalMode")}
                options={cosmosDBRetrievalOptions}
                required
                onChange={onCosmosDBRetrievalModeChange}
                selectedKey={cosmosDBRetrievalMode}
            />

            <Checkbox
                className={styles.chatSettingsSeparator}
                checked={shouldStream}
                label={i18next.t("settings.streamChatCompletionResponses")}
                onChange={onShouldStreamChange}
                disabled={onlyCitations}
            />

            <Checkbox
                className={styles.chatSettingsSeparator}
                checked={onlyCitations}
                label={i18next.t("settings.showOnlyCitations")}
                onChange={onOnlyCitationsChange}
            />

            <Checkbox
                className={styles.chatSettingsSeparator}
                checked={useAgenticKnowledgebase}
                label={i18next.t("settings.useAgenticRetrieval")}
                onChange={onUseAgenticKnowledgebaseChange}
            />

            <Dropdown
                className={styles.chatSettingsSeparator}
                label={i18next.t("settings.retrievalReasoningEffort")}
                options={retrievalReasoningOptions}
                required
                disabled={!useAgenticKnowledgebase}
                onChange={onRetrievalReasoningEffortChange}
                selectedKey={retrievalReasoningEffort}
            />

            <TextField
                className={styles.chatSettingsSeparator}
                label={i18next.t("settings.agenticRetrievalOutputSize")}
                value={agenticRetrievalOutputSize !== undefined ? String(agenticRetrievalOutputSize) : ""}
                onChange={onAgenticRetrievalOutputSizeChange}
                onBlur={onAgenticRetrievalOutputSizeBlur}
                disabled={!useAgenticKnowledgebase}
                placeholder={i18next.t("reasoningEffort.none")}
                type={"number"}
                min="5000"
                step="1"
            />

            <TextField
                className={styles.chatSettingsSeparator}
                label={i18next.t("settings.agenticRetrievalSourceDocumentCount")}
                value={agenticRetrievalSourceDocumentCount !== undefined ? String(agenticRetrievalSourceDocumentCount) : ""}
                onChange={onAgenticRetrievalSourceDocumentCountChange}
                onBlur={onAgenticRetrievalSourceDocumentCountBlur}
                disabled={!useAgenticKnowledgebase}
                placeholder={i18next.t("reasoningEffort.none")}
                type={"number"}
                min="50"
                max="200"
                step="1"
            />

            <TextField
                className={styles.chatSettingsSeparator}
                label={i18next.t("settings.agenticRetrievalDocumentCount")}
                value={agenticRetrievalDocumentCount !== undefined ? String(agenticRetrievalDocumentCount) : ""}
                onChange={onAgenticRetrievalDocumentCountChange}
                onBlur={onAgenticRetrievalDocumentCountBlur}
                disabled={!useAgenticKnowledgebase}
                placeholder={i18next.t("reasoningEffort.none")}
                type={"number"}
                min="1"
                max="200"
                step="1"
            />

            <details className={`${styles.chatSettingsSeparator} ${styles.settingsAccordion}`}>
                <summary className={styles.settingsAccordionSummary}>{i18next.t("settings.allProductSuggestionsGroupTitle")}</summary>
                <div className={styles.settingsAccordionContent}>
                    <Checkbox
                        className={styles.chatSettingsSeparator}
                        checked={enableAllProductForAllUsers}
                        label={i18next.t("settings.enableAllProductForAllUsers")}
                        onChange={onEnableAllProductForAllUsersChange}
                    />

                    <Checkbox
                        className={styles.chatSettingsSeparator}
                        checked={enableAllProductForUser}
                        label={i18next.t("settings.enableAllProductForUser")}
                        onChange={onEnableAllProductForUserChange}
                        disabled={!enableAllProductForAllUsers}
                    />

                    <Checkbox
                        className={styles.chatSettingsSeparator}
                        checked={allProductAutoSelectEnabled}
                        label={i18next.t("settings.allProductAutoSelectEnabled")}
                        onChange={onAllProductAutoSelectEnabledChange}
                    />

                    <SpinButton
                        className={styles.chatSettingsSeparator}
                        label={i18next.t("settings.allProductAutoSelectMinConfidence")}
                        min={0}
                        max={100}
                        step={1}
                        value={String(allProductAutoSelectMinConfidence)}
                        onChange={onAllProductAutoSelectMinConfidenceChange}
                    />

                    <SpinButton
                        className={styles.chatSettingsSeparator}
                        label={i18next.t("settings.allProductAutoSelectMinMargin")}
                        min={0}
                        max={100}
                        step={1}
                        value={String(allProductAutoSelectMinMargin)}
                        onChange={onAllProductAutoSelectMinMarginChange}
                    />

                    <SpinButton
                        className={styles.chatSettingsSeparator}
                        label={i18next.t("settings.allProductAutoSelectMinHits")}
                        min={1}
                        max={99}
                        step={1}
                        value={String(allProductAutoSelectMinHits)}
                        onChange={onAllProductAutoSelectMinHitsChange}
                    />

                    <SpinButton
                        className={styles.chatSettingsSeparator}
                        label={i18next.t("settings.allProductAutoSelectMinAvgRerankerScore")}
                        min={0}
                        max={10}
                        step={0.1}
                        value={String(allProductAutoSelectMinAvgRerankerScore)}
                        onChange={onAllProductAutoSelectMinAvgRerankerScoreChange}
                    />

                    <SpinButton
                        className={styles.chatSettingsSeparator}
                        label={i18next.t("settings.allProductAutoSelectMinAvgRerankerGap")}
                        min={0}
                        max={10}
                        step={0.1}
                        value={String(allProductAutoSelectMinAvgRerankerGap)}
                        onChange={onAllProductAutoSelectMinAvgRerankerGapChange}
                    />

                    <SpinButton
                        className={styles.chatSettingsSeparator}
                        label={i18next.t("settings.allProductAutoSelectWeightAverage")}
                        min={0}
                        max={1}
                        step={0.01}
                        value={String(allProductAutoSelectWeightAverage)}
                        onChange={onAllProductAutoSelectWeightAverageChange}
                    />

                    <SpinButton
                        className={styles.chatSettingsSeparator}
                        label={i18next.t("settings.allProductAutoSelectWeightPeak")}
                        min={0}
                        max={1}
                        step={0.01}
                        value={String(allProductAutoSelectWeightPeak)}
                        onChange={onAllProductAutoSelectWeightPeakChange}
                    />

                    <SpinButton
                        className={styles.chatSettingsSeparator}
                        label={i18next.t("settings.allProductAutoSelectWeightVolume")}
                        min={0}
                        max={1}
                        step={0.01}
                        value={String(allProductAutoSelectWeightVolume)}
                        onChange={onAllProductAutoSelectWeightVolumeChange}
                    />
                </div>
            </details>

            {hidetoken && <TokenClaimsDisplay />}
        </Panel>
    );
};
